<template>
  <div class="demo-video" :class="{ 'demo-video--compact': compact, 'demo-video--wide': wide }">
    <div v-if="resolvedTitle || resolvedSubtitle" class="demo-video__intro">
      <h2 v-if="resolvedTitle" class="demo-video__title">{{ resolvedTitle }}</h2>
      <p v-if="resolvedSubtitle" class="demo-video__subtitle">{{ resolvedSubtitle }}</p>
    </div>
    <div class="demo-video__frame">
      <iframe
        v-if="showVideo && isYouTube"
        :src="youtubeEmbedUrl"
        :title="iframeTitle"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
        referrerpolicy="strict-origin-when-cross-origin"
      />
      <video
        v-else-if="showVideo && useNativePlayer"
        class="demo-video__player"
        controls
        playsinline
        preload="metadata"
        :poster="posterUrl"
        :src="videoUrl"
      >
        Sorry, your browser doesn’t support embedded videos.
      </video>
      <iframe
        v-else-if="showVideo"
        :src="embedUrl"
        :title="iframeTitle"
        loading="lazy"
        allow="encrypted-media; fullscreen; microphone; screen-wake-lock;"
        allowfullscreen
        referrerpolicy="strict-origin-when-cross-origin"
      />
      <button
        v-else
        type="button"
        class="demo-video__placeholder"
        @click="loaded = true"
      >
        <span class="demo-video__play" aria-hidden="true">&#9658;</span>
        <span class="demo-video__placeholder-text">Load video</span>
        <span class="demo-video__placeholder-note">
          This video is hosted by {{ hostLabel }} and may set its own cookies.
          Click to load it.
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { eventBus } from '@/utils/eventBus';
import { hasMarketing } from '@/utils/consent';
import { getImproveVideo } from '@/config/videos';

const DEFAULT_VIDEO_MP4 =
  process.env.VUE_APP_DEMO_VIDEO_MP4 ||
  'https://synthesia-ttv-data.s3-eu-west-1.amazonaws.com/video_data/d0a7585d-afed-427d-9643-da8b6f9feff3/versions/5/transfers/rendered_video.mp4';

const DEFAULT_POSTER =
  process.env.VUE_APP_DEMO_VIDEO_POSTER ||
  'https://69jr5v75rc.execute-api.eu-west-1.amazonaws.com/prod/d0a7585d-afed-427d-9643-da8b6f9feff3/thumbnail.jpg';

const DEFAULT_DEMO_VIDEO =
  process.env.VUE_APP_DEMO_VIDEO_URL ||
  'https://share.synthesia.io/embeds/videos/d0a7585d-afed-427d-9643-da8b6f9feff3';

function extractYouTubeId(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (/^[\w-]{11}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|shorts\/|watch\?v=|live\/))([\w-]{11})/
  );
  return match ? match[1] : '';
}

export default {
  name: 'DemoVideo',
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    /** Catalog key from src/config/videos.js (preferred) */
    videoKey: { type: String, default: '' },
    /** YouTube ID or full YouTube URL */
    youtubeId: { type: String, default: '' },
    src: { type: String, default: '' },
    compact: { type: Boolean, default: false },
    /** Full container width (960px). Default is 760px centred: on a section
        page the video sits under the hero and at 960px it dominated the page
        (Steve, 15 Sep 2026). The homepage and Watch Demo, where the video IS
        the content, opt in to wide. */
    wide: { type: Boolean, default: false },
    /** Use third-party iframe (Synthesia) instead of the MP4 player */
    forceEmbed: { type: Boolean, default: false },
  },
  data() {
    return {
      loaded: false,
      consentTick: 0,
    };
  },
  computed: {
    catalog() {
      return this.videoKey ? getImproveVideo(this.videoKey) : null;
    },
    resolvedTitle() {
      return this.title || (this.catalog && this.catalog.title) || '';
    },
    resolvedSubtitle() {
      return this.subtitle || (this.catalog && this.catalog.subtitle) || '';
    },
    resolvedYouTubeId() {
      return (
        extractYouTubeId(this.youtubeId) ||
        extractYouTubeId(this.src) ||
        (this.catalog && this.catalog.id) ||
        ''
      );
    },
    isYouTube() {
      return !!this.resolvedYouTubeId;
    },
    youtubeEmbedUrl() {
      return (
        'https://www.youtube-nocookie.com/embed/' +
        this.resolvedYouTubeId +
        '?rel=0&modestbranding=1'
      );
    },
    videoUrl() {
      return this.src || DEFAULT_VIDEO_MP4;
    },
    posterUrl() {
      return DEFAULT_POSTER;
    },
    embedUrl() {
      return this.src || DEFAULT_DEMO_VIDEO;
    },
    useNativePlayer() {
      return !this.forceEmbed && !this.isYouTube;
    },
    showVideo() {
      return this.loaded || (this.consentTick >= 0 && hasMarketing());
    },
    hostLabel() {
      return this.isYouTube ? 'YouTube' : 'Synthesia';
    },
    iframeTitle() {
      return this.resolvedTitle
        ? this.resolvedTitle + ' - IMPROVE'
        : 'IMPROVE product video';
    },
  },
  created() {
    eventBus.$on('consent-updated', this.onConsentUpdated);
  },
  beforeDestroy() {
    eventBus.$off('consent-updated', this.onConsentUpdated);
  },
  methods: {
    onConsentUpdated() {
      this.consentTick += 1;
    },
  },
};
</script>

<style scoped>
.demo-video {
  width: 100%;
}

.demo-video__intro {
  margin-bottom: 16px;
  text-align: center;
}

.demo-video__title {
  font-family: 'Sora', sans-serif;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
}

.demo-video__subtitle {
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  margin: 0;
}

.demo-video__frame {
  position: relative;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  aspect-ratio: 1920 / 1080;
  overflow: hidden;
  border-radius: 8px;
  background: var(--brand-navy, #002458);
  box-shadow: 0 12px 32px rgba(0, 36, 88, 0.16);
}

.demo-video__frame iframe,
.demo-video__player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

.demo-video__player {
  object-fit: contain;
  background: #000;
}

.demo-video__placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  background: var(--brand-navy, #002458);
  color: #ffffff;
  font-family: 'Sora', sans-serif;
}

.demo-video__play {
  font-size: 42px;
  line-height: 1;
}

.demo-video__placeholder-text {
  font-size: 18px;
  font-weight: 600;
}

.demo-video__placeholder-note {
  font-size: 13px;
  opacity: 0.82;
  max-width: 360px;
  line-height: 1.4;
}

.demo-video__placeholder:focus-visible {
  outline: 2px solid #08a0a8;
  outline-offset: -4px;
}

.demo-video--wide .demo-video__frame {
  max-width: none;
}

.demo-video--compact .demo-video__title {
  font-size: 20px;
}

.demo-video--compact .demo-video__frame {
  box-shadow: 0 6px 18px rgba(0, 36, 88, 0.12);
}
</style>
